/// <reference types="vite/client" />
import { Outlet, useNavigate } from "react-router-dom";
import styles from "./components/Home/Home.module.css";
import BreadCrumbsScreen from "./pages/BreadCrumbs/BreadCrumbsScreen";
import ErrorMessageScreen from "./pages/ErrorMessage/ErrorMessageScreen";
import { selectDisplayErrorSnackBar } from "./features/userSlice";
import { useSelector } from "react-redux";
import { SharedStateProvider } from "./components/SharedStateProvider";
import { LicenseInfo } from "@mui/x-license";
import RouteTracker from "./components/RouteTracker/RouteTracker";
import { DrawerScreen } from "./pages/Drawer/DrawerScreen";
import { useEffect, useState } from "react";
import { handleTracingUserClick } from "./utils/trackingHandler";
import { usePreviousLocationTracker } from "./utils/ReferrerTracker";
import { getCurrentVersion } from "./version";
import NewVersionPopup from "./NewVersionPopup";
import { setNavigator } from "./utils/navigation";

const App = () => {
  usePreviousLocationTracker(); // Initializes tracking
  const messageForSnackBar = useSelector(selectDisplayErrorSnackBar);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [remindMeCount, setRemindMeCount] = useState(0);
  const [isCancelDisabled, setIsCancelDisabled] = useState(false);
  // Set the license key for MUI components
  // Ensure the license key is stored in the environment variable 'REACT_APP_MUI_LICENSE_KEY'
  LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY as string);

  useEffect(() => {
    document.addEventListener("click", handleTracingUserClick);
    return () => {
      document.removeEventListener("click", handleTracingUserClick);
    };
  }, []);

  const versionFileUrl = `/meta.json?timeStamp=${Date.now()}`;
  const navigate = useNavigate();

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch(versionFileUrl, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        const data = await res.json();
        if (data.version !== currentVersion) {
          setShowUpdatePrompt(true); // this triggers your UI
        }
      } catch (err) {
        console.error("Version check failed:", err);
      }
    };
    const interval = setInterval(checkVersion, 3600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [currentVersion, remindMeCount]);

  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const version = await getCurrentVersion(); // Fetch the version from your utility
        setCurrentVersion(version); // Set the fetched version to state
      } catch (error) {
        console.error("Failed to fetch version:", error);
      }
    };

    fetchVersion();
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleCancel = () => {
    const newCount = remindMeCount + 1;

    if (newCount >= 5) {
      setIsCancelDisabled(true);
    }

    setShowUpdatePrompt(false);
    setRemindMeCount(newCount);
  };

  return (
    <div className="app-container">
      <div className={styles.pageBuilding}>
        <div className={styles.pageLayout}>
          <NewVersionPopup
            open={showUpdatePrompt}
            onRefresh={handleRefresh}
            onCancel={handleCancel}
            isCancelDisabled={isCancelDisabled}
          />
          <RouteTracker />
          <DrawerScreen />
          <div
            style={{
              marginTop: "-4px",
              width: "100%",
              paddingLeft: "6px",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "block", width: "100%" }}>
              <ErrorMessageScreen
                message={messageForSnackBar?.message}
                type={messageForSnackBar?.type}
              />
            </div>
            <div className="bread-crumbs" style={{ marginBottom: "10px" }}>
              <BreadCrumbsScreen />
            </div>
            <SharedStateProvider>
              <Outlet />
            </SharedStateProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
export default App;
